/**
 * Waits for Adobe Analytics (_satellite) to be available before executing a callback.
 * This prevents race conditions where analytics events are fired before Adobe Launch has loaded.
 *
 * @param callback - The function to execute once Adobe Analytics is ready
 * @param timeout - Maximum time to wait in milliseconds (default: 5000ms)
 * @returns Promise that resolves when callback completes or rejects on timeout
 */
export const waitForAdobeAnalytics = async (
  callback: () => void,
  timeout = 5000
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const pollInterval = 100;

    const checkAdobeReady = () => {
      // Check if Adobe Launch's _satellite object is available
      if (typeof window !== 'undefined' && (window as any)._satellite) {
        try {
          callback();
          resolve();
        } catch (error) {
          reject(error);
        }
        return;
      }

      // Check if timeout exceeded
      if (Date.now() - startTime >= timeout) {
        console.warn(
          'Adobe Analytics not loaded within timeout, executing callback anyway'
        );
        try {
          callback();
          resolve();
        } catch (error) {
          reject(error);
        }
        return;
      }

      // Continue polling
      setTimeout(checkAdobeReady, pollInterval);
    };

    checkAdobeReady();
  });
};
